import React, { ElementType, forwardRef, FC } from 'react';
import {
  Box,
  Button,
  BoxProps,
  ButtonProps,
  IconButton,
  IconButtonProps,
  IconProps,
  Typography,
  TypographyProps,
} from '@mui/material';
import { CloseIcon } from '@/src/components/icon/stroke/CloseIcon';

export interface NotificationCenterProps extends BoxProps<ElementType> {
  /**
   * Title of the notification center
   */
  title?: React.ReactNode;
  /**
   * Custom props for the header
   */
  headerProps?: BoxProps<ElementType>;
  /**
   * Custom props for the title
   */
  titleProps?: TypographyProps<ElementType>;
  /**
   * Text for the dismiss all button
   */
  dismissAllText?: React.ReactNode;
  /**
   * Callback function when the dismiss all button is clicked
   */
  onDismissAll?: () => void;
  /**
   * Callback function when the close button is clicked
   */
  onClose?: () => void;
  /**
   * Max Height of the notification center
   */
  height?: number | string;
  /**
   * Custom props for the icon button
   */
  iconButtonProps?: IconButtonProps;
  /**
   * Custom props for the close icon
   */
  iconProps?: IconProps<ElementType>;
  /**
   * Custom props for the container
   */
  containerProps?: BoxProps<ElementType>;
  /**
   * Custom props for the dismiss box
   */
  dismissBoxProps?: BoxProps<ElementType>;
  /**
   * Custom props for the dismiss button
   */
  dismissButtonProps?: ButtonProps<ElementType>;
}

export const NotificationCenter: FC<NotificationCenterProps> = forwardRef(
  (
    {
      children,
      sx,
      title = 'Notifications',
      headerProps,
      titleProps,
      dismissAllText = 'Dismiss All',
      onDismissAll,
      onClose,
      iconButtonProps,
      iconProps,
      containerProps,
      dismissBoxProps,
      dismissButtonProps,
      height = 556,
      ...rest
    },
    ref,
  ) => {
    const { sx: headerSx, ...restHeader } = headerProps ?? {};
    const { sx: containerSx, ...restContainer } = containerProps ?? {};
    const { sx: dismissBoxSx, ...restDismissBox } = dismissBoxProps ?? {};
    const { sx: iconButtonSx, ...restIconButton } = iconButtonProps ?? {};

    const formatHeight = (heightValue: number | string) => {
      if (typeof heightValue === 'string' && heightValue.includes('px')) {
        return heightValue;
      }
      // if typeof heightValue === 'number', or string without 'px'
      return `${heightValue}px`;
    };

    return (
      <Box
        ref={ref}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: { xs: '100%', sm: '360px', md: '440px' },
          borderRadius: { xs: '0px', sm: '8px' },
          backgroundColor: 'background.primary',
          color: 'text.primary',
          boxShadow: '0px 5px 6px 1px rgba(0, 0, 0, 0.18), 0px -1px 4px 0px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden',
          maxHeight: formatHeight(height),
          ...sx,
        }}
        data-testid="NotificationCenter"
        {...rest}
      >
        <Box
          sx={{
            padding: '16px 8px 16px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            ...headerSx,
          }}
          {...restHeader}
        >
          <Typography variant="h4" component="span" data-testid="NotificationCenter.title" {...titleProps}>
            {title}
          </Typography>
          {onClose && (
            <IconButton
              aria-label="onClose notificationCenter"
              onClick={onClose}
              size="medium"
              className="notificationCenter-onClose"
              sx={{
                padding: '4px',
                ...iconButtonSx,
              }}
              data-testid="NotificationCenter.onClose"
              {...restIconButton}
            >
              <CloseIcon fontSize="medium" className="ballotx" {...iconProps} />
            </IconButton>
          )}
        </Box>

        {onDismissAll && (
          <Box
            sx={{
              padding: '0 8px 4px 16px',
              display: 'flex',
              justifyContent: 'flex-end',
              ...dismissBoxSx,
            }}
            {...restDismissBox}
          >
            <Button
              onClick={onDismissAll}
              size="small"
              color="secondary"
              variant="text"
              data-testid="NotificationCenter.dismiss"
              {...dismissButtonProps}
            >
              {dismissAllText}
            </Button>
          </Box>
        )}
        <Box
          sx={{
            overflowY: 'auto',
            // add divider between notifications
            '& [data-testid="Notification"]:not(:last-child)': {
              borderBottom: 1,
              borderColor: 'surface.secondary',
            },
            ...containerSx,
          }}
          data-testid="NotificationCenter.content"
          {...restContainer}
        >
          {children}
        </Box>
      </Box>
    );
  },
);
